import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Chip,
  Card,
  CardContent,
  Button,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  CircularProgress,
  Grid,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PersonAdd as PersonAddIcon,
  Save as SaveIcon,
  School as SchoolIcon,
  Class as ClassIcon,
  LibraryBooks as LibraryBooksIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import { toast } from 'react-toastify';
import { adminHasPermission } from '../../utils/adminPermissions';

const TutorForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const canUpdateTutors = adminHasPermission('update-tutors');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  const [courses, setCourses] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [availableSchoolYears, setAvailableSchoolYears] = useState([]);

  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [selectedSubjectIdsByCourseId, setSelectedSubjectIdsByCourseId] = useState({});
  const [preferredSchoolYearIds, setPreferredSchoolYearIds] = useState([]);
  const [preferredSubjectIds, setPreferredSubjectIds] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!canUpdateTutors) {
      toast.error('You do not have permission to manage tutors.');
      navigate('/admin/tutors');
      return;
    }
    fetchData();
  }, [canUpdateTutors, navigate, id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 1. Fetch lookup data
      const resLookup = await api.get('admin/tutors/courses-for-assignment');
      if (resLookup.data?.success) {
        setCourses(resLookup.data.data.courses || []);
        setAvailableSubjects(resLookup.data.data.subjects || []);
        setAvailableSchoolYears(resLookup.data.data.school_years || []);
      }

      // 2. Fetch tutor data if in edit mode
      if (isEdit) {
        const resTutor = await api.get(`admin/tutors/${id}`);
        if (resTutor.data?.success) {
          const tutor = resTutor.data.data;
          setFormData({
            firstName: tutor.first_name,
            lastName: tutor.last_name,
            email: tutor.email,
          });
          setSelectedCourseIds(tutor.course_ids || []);
          setSelectedSubjectIdsByCourseId(tutor.course_subject_ids_by_course || {});
          setPreferredSchoolYearIds(tutor.preferred_school_year_ids || []);
          setPreferredSubjectIds(tutor.preferred_subject_ids || []);
        } else {
          toast.error('Failed to load tutor details');
          navigate('/admin/tutors');
        }
      }
    } catch (e) {
      toast.error('Failed to load form data');
      if (isEdit) navigate('/admin/tutors');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCourse = (courseId) => {
    setSelectedCourseIds((prev) => {
      const isSelected = prev.includes(courseId);
      const next = isSelected ? prev.filter((id) => id !== courseId) : [...prev, courseId];

      const courseKey = String(courseId);
      setSelectedSubjectIdsByCourseId((prevMap) => {
        const copy = { ...prevMap };
        if (isSelected) {
          delete copy[courseKey];
        } else {
          copy[courseKey] = copy[courseKey] ?? [];
        }
        return copy;
      });

      return next;
    });
  };

  const handleSelectAllCourses = (checked) => {
    if (checked) {
      const allIds = courses.map((c) => c.id);
      setSelectedCourseIds(allIds);
    } else {
      setSelectedCourseIds([]);
      setSelectedSubjectIdsByCourseId({});
    }
  };

  const handleToggleSubjectInCourse = (courseId, subjectId) => {
    const courseKey = String(courseId);
    setSelectedSubjectIdsByCourseId((prev) => {
      const current = prev[courseKey] ?? [];
      const next = current.includes(subjectId)
        ? current.filter((id) => id !== subjectId)
        : [...current, subjectId];
      return { ...prev, [courseKey]: next };
    });
  };

  const handleTogglePreference = (id, list, setList) => {
    setList((prev) => 
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const { firstName, lastName, email } = formData;
    if (!firstName || !lastName || !email) {
      toast.error('Please fill in all required fields.');
      return;
    }

    const courseSubjects = selectedCourseIds.map((course_id) => ({
      course_id,
      subject_ids: selectedSubjectIdsByCourseId[String(course_id)] ?? [],
    }));

    const payload = {
      first_name: firstName,
      last_name: lastName,
      email,
      preferred_school_year_ids: preferredSchoolYearIds,
      preferred_subject_ids: preferredSubjectIds,
      course_subjects: courseSubjects,
    };

    setSaving(true);
    try {
      const res = isEdit 
        ? await api.put(`admin/tutors/${id}`, payload)
        : await api.post('admin/tutors', payload);
        
      if (res.data?.success) {
        toast.success(isEdit ? 'Tutor updated successfully' : 'Tutor created successfully. Credentials sent via email.');
        navigate('/admin/tutors');
      } else {
        toast.error(res.data?.message || 'Failed to save tutor');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save tutor');
    } finally {
      setSaving(false);
    }
  };

  const allCoursesSelected = courses.length > 0 && selectedCourseIds.length === courses.length;

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{
      p: 3,
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
    }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/admin/tutors')}
        sx={{
          mb: 3,
          color: '#5a6c7d',
          fontWeight: 600,
          '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }
        }}
      >
        Back to Tutor List
      </Button>

      <Box sx={{ mb: 4 }}>
        <Box sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '25px',
          px: 3,
          py: 1,
          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.2)'
        }}>
          {isEdit ? <EditIcon sx={{ color: 'white' }} /> : <PersonAddIcon sx={{ color: 'white' }} />}
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
            {isEdit ? 'Edit Tutor Profile' : 'Add New Tutor'}
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: '#5a6c7d', ml: 1 }}>
          {isEdit 
            ? `Update profile details and assignments for ${formData.firstName} ${formData.lastName}.`
            : 'Create a new tutor account. Login credentials and portal URL will be sent via email.'
          }
        </Typography>
      </Box>

      <Card sx={{
        borderRadius: 4,
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(20px)',
        mb: 4
      }}>
        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleSave}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2d3748' }}>
                  1. Profile Details
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="First Name"
                  required
                  fullWidth
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Last Name"
                  required
                  fullWidth
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField
                  label="Email"
                  required
                  fullWidth
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  helperText={!isEdit && "Password will be automatically generated and emailed."}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: 600, color: '#2d3748' }}>
                  2. Preferred Levels & Subjects
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ClassIcon color="action" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Preferred School Years</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {availableSchoolYears.map((year) => (
                    <Chip
                      key={year.id}
                      label={year.name}
                      onClick={() => handleTogglePreference(year.id, preferredSchoolYearIds, setPreferredSchoolYearIds)}
                      color={preferredSchoolYearIds.includes(year.id) ? "primary" : "default"}
                      variant={preferredSchoolYearIds.includes(year.id) ? "filled" : "outlined"}
                    />
                  ))}
                  {availableSchoolYears.length === 0 && <Typography variant="body2" color="text.secondary">Loading school years...</Typography>}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LibraryBooksIcon color="action" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Preferred Subjects</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {availableSubjects.map((subject) => (
                    <Chip
                      key={subject.id}
                      label={subject.name}
                      onClick={() => handleTogglePreference(subject.id, preferredSubjectIds, setPreferredSubjectIds)}
                      color={preferredSubjectIds.includes(subject.id) ? "primary" : "default"}
                      variant={preferredSubjectIds.includes(subject.id) ? "filled" : "outlined"}
                    />
                  ))}
                  {availableSubjects.length === 0 && <Typography variant="body2" color="text.secondary">Loading subjects...</Typography>}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: 600, color: '#2d3748' }}>
                  3. Optional: Assign Specific Courses
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </Grid>

              <Grid item xs={12}>
                {loading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allCoursesSelected}
                          indeterminate={selectedCourseIds.length > 0 && selectedCourseIds.length < courses.length}
                          onChange={(e) => handleSelectAllCourses(e.target.checked)}
                        />
                      }
                      label="Select all courses"
                      sx={{ mb: 1 }}
                    />
                    <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', p: 2, borderRadius: 2 }}>
                      <List dense>
                        {courses.map((course) => {
                          const isSelected = selectedCourseIds.includes(course.id);
                          const selectedSubjectIds = selectedSubjectIdsByCourseId[String(course.id)] ?? [];

                          return (
                            <Box key={course.id}>
                              <ListItem disablePadding>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={() => handleToggleCourse(course.id)}
                                    />
                                  }
                                  label={course.name}
                                />
                              </ListItem>

                              {isSelected && Array.isArray(course.subjects) && course.subjects.length > 0 && (
                                <Box sx={{ pl: 4, pr: 1, pb: 1 }}>
                                  <Grid container spacing={1}>
                                    {course.subjects.map((subject) => (
                                      <Grid item key={subject.id}>
                                        <Chip
                                          label={subject.name}
                                          onClick={() => handleToggleSubjectInCourse(course.id, subject.id)}
                                          color={selectedSubjectIds.includes(subject.id) ? "primary" : "default"}
                                          variant={selectedSubjectIds.includes(subject.id) ? "filled" : "outlined"}
                                          size="small"
                                          sx={{ borderRadius: '6px' }}
                                        />
                                      </Grid>
                                    ))}
                                  </Grid>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </List>
                      {courses.length === 0 && (
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          No courses available.
                        </Typography>
                      )}
                    </Paper>
                  </>
                )}
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/admin/tutors')}
                    disabled={saving}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={saving || !canUpdateTutors}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      px: 4,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                      }
                    }}
                  >
                    {saving ? 'Saving...' : (isEdit ? 'Update Tutor' : 'Create Tutor')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TutorForm;
